import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, Clock, Tag, ArrowLeft } from "lucide-react";
import { VideoCard } from "../components/Cards";
import ProgramVideoSection from "../components/ProgramVideoSection";
import { getProgram, getVideos } from "../lib/api";
import { optimizeImageUrl, HERO_PAGE_WIDTH } from "../lib/images";
import { usePageTitle } from "../hooks/usePageTitle";

export default function ProgramDetail() {
  const { slug } = useParams();
  const [program, setProgram] = useState(null);
  const [videos, setVideos] = useState([]);
  const [notFound, setNotFound] = useState(false);

  usePageTitle(program?.name);

  useEffect(() => {
    window.scrollTo(0, 0);
    setNotFound(false);
    getProgram(slug)
      .then((p) => { setProgram(p); return getVideos(p.id); })
      .then(setVideos)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="pt-[140px] pb-24 tv-container text-center">
      <p className="text-white text-xl">Program bulunamadı.</p>
      <Link to="/programlar" className="tv-btn-primary mt-6 inline-flex">Programlara Dön</Link>
    </div>
  );
  if (!program) return <div className="pt-[200px] pb-40" />;

  return (
    <div>
      <section className="relative pt-[74px] overflow-hidden">
        <div className="relative h-[380px] md:h-[440px]">
          <img
            src={optimizeImageUrl(program.coverImage, HERO_PAGE_WIDTH)}
            alt={program.name}
            loading="eager"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#06060d] via-[#06060d]/85 to-[#06060d]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#06060d]/90 to-transparent" />
          <div className="relative tv-container h-full flex flex-col justify-end pb-10">
            <Link to="/programlar" className="inline-flex items-center gap-2 text-sm text-[var(--tv-muted)] hover:text-white mb-4 w-fit transition-colors">
              <ArrowLeft size={16} /> Tüm Programlar
            </Link>
            <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="tv-badge mb-3">{program.category}</span>
              <h1 className="font-display font-extrabold text-4xl md:text-5xl text-white">{program.name}</h1>
              <div className="flex flex-wrap gap-5 mt-4 text-sm text-[var(--tv-muted)]">
                <span className="flex items-center gap-2"><CalendarDays size={16} className="text-[var(--tv-purple)]" /> {program.day}</span>
                <span className="flex items-center gap-2"><Clock size={16} className="text-[var(--tv-purple)]" /> {program.time}</span>
                <span className="flex items-center gap-2"><Tag size={16} className="text-[var(--tv-purple)]" /> {program.category}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="tv-container py-12">
        <div className="tv-card p-6 md:p-8 mb-12">
          <h2 className="text-xl font-bold text-white font-display mb-3">Program Hakkında</h2>
          <p className="text-[var(--tv-muted)] leading-relaxed">{program.longDescription || program.description}</p>
        </div>

        <h2 className="tv-section-title text-white mb-6">
          <span className="tv-section-bar" /> Program Videoları
        </h2>
        <ProgramVideoSection videos={videos} />
      </div>
    </div>
  );
}
